import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Normalize a stored video URL into something the browser can actually play:
// - Object storage paths (/.private/..., ./public/...) → routed through /objects proxy
// - Dropbox share links (www.dropbox.com serves an HTML preview page) → dl.dropboxusercontent.com raw stream
// Used by VideoPlayer/VideoThumbnailCard so ALL video surfaces handle every source format.
export function normalizeVideoUrl(videoUrl: string | null | undefined): string | undefined {
  if (!videoUrl) return undefined;

  const isStoragePath = videoUrl.startsWith('/.private/') ||
                        videoUrl.startsWith('./.private/') ||
                        videoUrl.startsWith('/public/') ||
                        videoUrl.startsWith('./public/');

  if (isStoragePath && !videoUrl.startsWith('/objects/')) {
    const cleanPath = videoUrl.startsWith('./') ? videoUrl.slice(1) : videoUrl;
    return `/objects${cleanPath}`;
  } else if (videoUrl.includes('dropbox.com')) {
    // Convert Dropbox shared-link to the direct-content CDN hostname.
    // dl.dropboxusercontent.com serves the raw byte stream — www.dropbox.com
    // returns an HTML preview/redirect page that breaks <video> elements.
    try {
      const url = new URL(videoUrl);
      url.hostname = 'dl.dropboxusercontent.com';
      url.searchParams.delete('dl');
      url.searchParams.delete('raw');
      return url.toString();
    } catch (e) {
      return videoUrl
        .replace('www.dropbox.com', 'dl.dropboxusercontent.com')
        .replace(/[?&]dl=\d/, '')
        .replace(/[?&]raw=\d/, '');
    }
  }
  return videoUrl;
}

/**
 * Returns true when the URL points to a playable video file
 * (mp4/webm/ogg/mov/m4v or Replit Object Storage /objects/ path).
 *
 * Returns false for web-page inspection report URLs such as those provided
 * by Sohnen (e.g. https://monkfish-app-phmcr.ondigitalocean.app/main/ViewItem/…).
 */
export function isDirectVideoUrl(url: string | null | undefined): boolean {
  if (!url || url.trim() === '') return false;
  const u = url.toLowerCase();
  return (
    u.includes('.mp4') ||
    u.includes('.webm') ||
    u.includes('.ogg') ||
    u.includes('.mov') ||
    u.includes('.m4v') ||
    u.includes('/objects/')
  );
}
